import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { LocalFlorist, Grass, Home, Landscape, Agriculture } from '@mui/icons-material';
import StartWithPlant from '../components/onboarding/StartWithPlant';
import StartWithGarden from '../components/onboarding/StartWithGarden';
import StartWithArea from '../components/onboarding/StartWithArea';
import StartWithSpace from '../components/onboarding/StartWithSpace';
import StartWithFarm from '../components/onboarding/StartWithFarm';

type StartOption = 'plant' | 'garden' | 'area' | 'space' | 'farm' | null;

const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedOption, setSelectedOption] = useState<StartOption>(null);

  const options = [
    {
      id: 'plant' as StartOption,
      icon: <LocalFlorist sx={{ fontSize: 48 }} />,
      title: 'Start with a Plant',
      description: 'Perfect for beginners. Track individual plants on your windowsill or balcony.',
      color: '#4ade80',
    },
    {
      id: 'garden' as StartOption,
      icon: <Grass sx={{ fontSize: 48 }} />,
      title: 'Create a Garden',
      description: 'Have multiple plants? Organize them into a garden like a planter box or bed.',
      color: '#22c55e',
    },
    {
      id: 'area' as StartOption,
      icon: <Home sx={{ fontSize: 48 }} />,
      title: 'Define an Area',
      description: 'Group multiple gardens in one location like your patio or balcony.',
      color: '#16a34a',
    },
    {
      id: 'space' as StartOption,
      icon: <Landscape sx={{ fontSize: 48 }} />,
      title: 'Map a Space',
      description: 'Define indoor vs outdoor spaces, or different floors in a building.',
      color: '#15803d',
    },
    {
      id: 'farm' as StartOption,
      icon: <Agriculture sx={{ fontSize: 48 }} />,
      title: 'Establish a Farm',
      description: 'Set up a complete property with GPS location and invite collaborators.',
      color: '#14532d',
    },
  ];

  const handleComplete = () => {
    navigate('/dashboard');
  };

  if (selectedOption) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
        <Container maxWidth="md">
          <Button
            onClick={() => setSelectedOption(null)}
            sx={{ mb: 3 }}
          >
            ← Back to options
          </Button>
          
          {selectedOption === 'plant' && <StartWithPlant onComplete={handleComplete} />}
          {selectedOption === 'garden' && <StartWithGarden onComplete={handleComplete} />}
          {selectedOption === 'area' && <StartWithArea onComplete={handleComplete} />}
          {selectedOption === 'space' && <StartWithSpace onComplete={handleComplete} />}
          {selectedOption === 'farm' && <StartWithFarm onComplete={handleComplete} />}
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Container maxWidth="lg">
        <Box textAlign="center" mb={6}>
          <Typography variant="h3" gutterBottom fontWeight="bold">
            Welcome to Botala! 🌱
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
            How would you like to start your gardening journey?
          </Typography>
          
          {!isMobile && (
            <Stepper activeStep={-1} alternativeLabel sx={{ mb: 4 }}>
              <Step>
                <StepLabel>Plant</StepLabel>
              </Step>
              <Step>
                <StepLabel>Garden</StepLabel>
              </Step>
              <Step>
                <StepLabel>Area</StepLabel>
              </Step>
              <Step>
                <StepLabel>Space</StepLabel>
              </Step>
              <Step>
                <StepLabel>Farm</StepLabel>
              </Step>
            </Stepper>
          )}
        </Box>

        <Grid container spacing={3}>
          {options.map((option) => (
            <Grid item xs={12} sm={6} md={4} lg={2.4} key={option.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
                onClick={() => setSelectedOption(option.id)}
              >
                <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                  <Box sx={{ color: option.color, mb: 2 }}>
                    {option.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    {option.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {option.description}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    fullWidth
                    variant="contained"
                    sx={{
                      bgcolor: option.color,
                      '&:hover': { bgcolor: option.color, opacity: 0.9 },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedOption(option.id);
                    }}
                  >
                    Choose This
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box textAlign="center" mt={6}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Don't worry! You can always expand later. Start small and grow at your own pace.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            <strong>Examples:</strong><br />
            🌱 <strong>Plant:</strong> A basil plant on your kitchen windowsill<br />
            🌿 <strong>Garden:</strong> A raised bed or planter box with multiple plants<br />
            🏡 <strong>Area:</strong> Your patio with multiple planter boxes<br />
            🏞️ <strong>Space:</strong> Your entire backyard (outdoor) or apartment (indoor)<br />
            🚜 <strong>Farm:</strong> Your whole property or a community garden
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Onboarding;